import React, { useState } from 'react';

import { Upload, Loader2 } from 'lucide-react';

import { showAlert } from '../../utils/sweetAlertUtils';

interface FileUploadProps {
  id: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  title?: string;
  subTitle?: string;
  allowMultiple?: boolean;
  accept?: string[];
  required?: boolean;
  error?: string;
  isUploading?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
  id,
  onChange,
  title = 'Upload media or drag and drop',
  allowMultiple = false,
  accept = [],
  required = false,
  error,
  isUploading = false,
}) => {
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      if (accept.length > 0) {
        const invalid = Array.from(e.target.files).filter((file) => {
          const ext = '.' + file.name.split('.').pop()?.toLowerCase();
          return !accept.map((a) => a.toLowerCase()).includes(ext);
        });
        if (invalid.length > 0) {
          e.target.value = '';
          setUploadedFileName(null);
          showAlert({
            title: 'Invalid file type',
            text: `The following file${invalid.length > 1 ? 's are' : ' is'} not allowed: ${invalid.map((f) => f.name).join(', ')}. Allowed types: ${accept.join(', ')}`,
            icon: 'warning',
          });
          return;
        }
      }
      const fileNames = Array.from(e.target.files)
        .map((file) => file.name)
        .join(', ');
      setUploadedFileName(fileNames);
      onChange(e);
    }
  };

  // Accept string for <input>
  const acceptString = accept.join(',');

  const derivedSubtitle =
    accept.length > 0 ? `Allowed file types: ${accept.join(', ')}` : '';

  return (
    <div className="relative my-6 col-span-full">
      <input
        id={id}
        name={id}
        type="file"
        className="hidden"
        onChange={handleFileChange}
        multiple={allowMultiple}
        accept={acceptString}
        required={required}
      />
      <label
        htmlFor={id}
        className={`relative flex flex-col items-center gap-2 px-3 py-6 text-sm font-medium text-center 
          transition-colors border border-dashed rounded
          ${isUploading 
            ? 'border-secondary/30 bg-secondary/10 dark:border-secondary/40 dark:bg-secondary/20 cursor-wait' 
            : error 
            ? 'border-red-300 hover:bg-red-50 cursor-pointer' 
            : 'border-slate-300 hover:bg-slate-50 cursor-pointer'
          }`}
      >
        <span className={`inline-flex items-center justify-center h-12 px-3 rounded-full ${isUploading ? 'bg-secondary/20 text-secondary dark:bg-secondary/30' : 'bg-slate-100/70 text-slate-400'}`}>
          {isUploading ? (
            <Loader2 className="w-6 h-6 animate-spin" aria-label="Uploading" />
          ) : (
            <Upload className="w-6 h-6" aria-label="File input icon" />
          )}
        </span>
        <span className="text-slate-500">
          {isUploading ? 'Uploading...' : title}
          {required && <span className="text-red-500 ml-1">*</span>}
        </span>
        {derivedSubtitle && !isUploading && (
          <span className="text-xs text-slate-400">{derivedSubtitle}</span>
        )}
        {!isUploading && (
          <span className="text-xs text-slate-400">Maximum file size: 100MB</span>
        )}
        {isUploading && (
          <span className="mt-2 text-xs text-secondary dark:text-secondary/90 font-medium">
            Please wait while the file is being uploaded...
          </span>
        )}
        {!isUploading && uploadedFileName && (
          <span className="mt-2 text-xs text-emerald-500">
            {uploadedFileName} ready to be uploaded
          </span>
        )}
        {!isUploading && error && (
          <span className="mt-2 text-xs text-red-500">{error}</span>
        )}
      </label>
    </div>
  );
};

export default FileUpload;
